<template>
  <div class="d-flex align-center ga-2">
    <EmployeeAcknowledgementActionsSkeleton v-if="libraryLoading" />
    <template v-else>
      <HideRbac :permissions="['downloadJobProfileAcknowledgementCsv']">
        <v-btn
          variant="outlined"
          height="36"
          rounded="lg"
          class="download-btn"
          :loading="csvDownloadLoading"
          :disabled="libraryLoading || csvDownloadLoading || hasNoResults"
          @click="handleDownloadCsv"
        >
          <v-icon
            icon="$tray-arrow-down"
            class="mr-2"
            size="small"
          />
          Download CSV
        </v-btn>
      </HideRbac>

      <v-btn
        color="brandPrimary"
        :disabled="!selectedCount || bulkReminderLoading || !primaryButtonPermissionGranted"
        variant="flat"
        height="36"
        rounded="lg"
        :loading="bulkReminderLoading"
        @click="handlePrimaryAction"
      >
        {{ primaryButtonLabel }}
      </v-btn>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useTracking } from '@/stores/tracking/tracking';
import { useAppAuthData } from '@/stores/app/authData';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useEmployeeAcknowledgementsStore } from '@/stores/jobProfiles/employeeAcknowledgements';
import EmployeeAcknowledgementActionsSkeleton
  from '@/components/library/employeeAcknowledgements/EmployeeAcknowledgementActionsSkeleton.vue';
import HideRbac from '@/components/rbac/HideRbac.vue';

const route = useRoute();

defineProps({
  libraryLoading: { type: Boolean, default: false },
  selectedCount: { type: Number, default: 0 },
});

const {
  selectedEmployeesForBulkCreate,
  bulkReminderLoading,
  csvDownloadLoading,
  employeeAcknowledgements,
} = storeToRefs(useEmployeeAcknowledgementsStore());

const hasNoResults = computed(() => !employeeAcknowledgements.value?.length);

const {
  openBulkCreateModal,
  openBulkSendReminderModal,
  downloadEmployeeAcknowledgementsCsv,
} = useEmployeeAcknowledgementsStore();

const allSelectedArePending = computed(() => {
  const employees = selectedEmployeesForBulkCreate.value;

  if (!employees?.length) return false;

  return employees.every((e) => e.acknowledgement_alert === 'pending_acknowledgement');
});

const primaryButtonPermissionGranted = computed(() => useAppAuthData().checkRbacPermissions([
  allSelectedArePending.value ? 'sendJobProfileAcknowledgementReminders' : 'createEmployeeJobProfileAcknowledgements',
]));

const primaryButtonLabel = computed(() => {
  const count = selectedEmployeesForBulkCreate.value?.length ?? 0;

  if (count === 0) return 'Create Acknowledgement';

  if (allSelectedArePending.value) {
    return count === 1 ? 'Send Reminder' : 'Send Reminders';
  }

  return count === 1 ? 'Create Acknowledgement' : 'Create Acknowledgements';
});

const handlePrimaryAction = () => {
  if (allSelectedArePending.value) {
    useTracking().ackLibClickedRemind({ location: 'Bulk Button on Table' });

    openBulkSendReminderModal();
  } else {
    useTracking().ackLibClickedCreate({ location: 'Bulk Button on Table' });

    openBulkCreateModal();
  }
};

const handleDownloadCsv = () => {
  useTracking().ackLibDownloadedCsv();

  downloadEmployeeAcknowledgementsCsv(route.query);
};
</script>

<style lang="less" scoped>
.download-btn {
  border: 1px solid @stroke-default;
  color: @text-dark;
  background: @white;
}
</style>
